/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Select from 'react-select/';
import { Button, Form, FormGroup, Label } from 'reactstrap';
import { duplicateSurvey, reloadSurveyListSummary } from '../../actions/survey';
import { ROUTES } from '../../constants/routes';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { reloadSurveyList } from '../../slice/initiativeSurveyListSlice';
import { getMonthNonJSOptions, getYearDropdownOptions } from '../../utils/date';
import { BasicAlert } from '../alert/BasicAlert';
import { SubmitButton } from '../button/SubmitButton';
import { CheckboxInput } from '../form/CheckboxInput';
import { CheckboxProps, ConfigFormData, updateFn } from '../form/common';
import Loader from '../loader';
import { searchReportingInitiativesExtended } from '../../actions/user';
import {
  createSurveyDateEntry,
  InitiativeSuggestionState,
  uniqueSurveyDates
} from '../../constants/initiative';
import { generateUrl } from '../../routes/util';
import { QUESTION } from '../../constants/terminology';
import { DEFAULT_YEAR_RANGE } from '../../constants/date';

interface SurveyDuplicateFormProps {
  survey: any;
  toggle: () => void;
}

const SurveyDuplicateForm = ({ survey, toggle }: SurveyDuplicateFormProps) => {

  const [form, setForm] = useState<ConfigFormData>({
    year: '', month: '', delegation: false, settings: false, scope: false, hasData: false
  })

  const [{ loaded, initiatives }, setInitiatives] = useState<InitiativeSuggestionState>({
    loaded: false,
    initiatives: [],
  });

  useEffect(() => {
    searchReportingInitiativesExtended()
      .then((d) => setInitiatives({ loaded: true, initiatives: d }))
      .catch(() => setInitiatives({ loaded: true, initiatives: [] }))
  }, []);

  const [saving, setSaving] = useState(false);
  const [errorMessage, setMessage] = useState('');
  const dispatch = useDispatch();
  const history = useHistory();

  const monthOptions = getMonthNonJSOptions();
  const yearOptions = getYearDropdownOptions(DEFAULT_YEAR_RANGE);
  const selectedMonth = monthOptions.find((o: any) => o.value === form.month)
  const selectedYear = yearOptions.find((o: any) => o.value === form.year)

  const existingSurveys = useMemo(() => uniqueSurveyDates(initiatives), [initiatives]);
  const jsMonth = Number(form.month) - 1; // Back to start from 0
  const hasConflict = existingSurveys.has(createSurveyDateEntry(survey.initiativeId, Number(form.year), jsMonth))

  const updateForm: updateFn = (update) => {
    if (update.code === 'scope' && update.value === false) {
      setForm({ ...form, delegation: false, hasData: false, [update.code]: update.value })
      return
    }
    setForm({
      ...form,
      [update.code]: update.value
    })
  }

  const checkOptions: CheckboxProps = {
    groupCode: 'Report Survey Options',
    form: {},
    options: [
      {
        code: 'settings',
        name: 'Settings',
        instructions: 'Copy over survey settings such as \'Verification Required\' or \'Evidence Required\'.',
      },
      {
        code: 'scope',
        name: 'Scope',
        instructions: 'Re-apply the same SDGs, standards, frameworks or Custom Metrics to scope.',
      },
      {
        instructions: `Re-assign survey and ${QUESTION.SINGULAR} delegation to the same administrators, contributors, verifiers and monitors.`,
        code: 'delegation',
        name: 'Delegation',
        disabled: !form.scope
      },
      {
        instructions: `Make a copy of the latest data entries for the ${QUESTION.PLURAL}. All data will be re-assigned to be owned by you, and will not be auto-verified.`,
        code: 'hasData',
        name: 'Data',
        disabled: !form.scope
      }
    ],
    onChange: () => { },
  }

  const requiredFields: (keyof ConfigFormData)[] = [
    'year',
    'month',
  ];


  const isConfirmBtnDisabled = (form: ConfigFormData) => {
    if (hasConflict) {
      return true;
    }

    return requiredFields.some((f) => undefined === form[f] || form[f] === '');
  };

  const isConfirmDisabled = saving || isConfirmBtnDisabled(form) || !!errorMessage;

  const handleClick = async () => {
    setSaving(true)
    try {
      const createdSurvey = await duplicateSurvey(survey._id, form)
      setSaving(false)
      const analytics = getAnalytics();
      await analytics.track(AnalyticsEvents.SurveyDuplicate, {
        surveyId: createdSurvey._id,
        initiativeId: createdSurvey.initiativeId
      });
      toggle();
      dispatch(reloadSurveyList())
      dispatch(reloadSurveyListSummary());
      if (createdSurvey._id) {
        history.push(
          generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId: createdSurvey.initiativeId, surveyId: createdSurvey._id, page: 'scope' })
        );
      }
    } catch (e) {
      setSaving(false)
      setMessage(e.message)
    }
  }

  if (!loaded) {
    return <Loader />;
  }

  return <>
    <BasicAlert type={'danger'}>{errorMessage}</BasicAlert>
    {saving && <Loader />}
    <Form className='text-ThemeAccentExtradark'>
      <FormGroup>
        <div className='row'>
          <div className='col'>
            <Label className='d-block form-check-label subtitle-container strong' for={'reporting-company'}>
              Reporting Period
            </Label>
            <Label className='d-block subtitle-container' for={'reporting-company'}>
              Please select the reporting period for the new report survey.
            </Label>
          </div>
        </div>
        <div className='row mt-2'>
          <BasicAlert hide={!hasConflict} type={'warning'} >
            <div>
              <b>WARNING:</b> A Report survey already exists for the selected Period. Please update your settings.
            </div>
          </BasicAlert>
          <div className='col-12 col-md-6'>
            <Select
              placeholder={'Select Month'}
              className='w-100 valueList-react-select'
              onChange={(option: any) => updateForm({ code: 'month', value: option?.value })}
              value={selectedMonth}
              isSearchable={true}
              options={monthOptions}
            />
          </div>
          <div className='col-12 col-md-6'>
            <Select
              placeholder={'Select Year'}
              className='w-100 valueList-react-select'
              onChange={(option: any) => updateForm({ code: 'year', value: option.value })}
              value={selectedYear}
              isSearchable={true}
              options={yearOptions}
            />
          </div>
        </div>
      </FormGroup>
      <FormGroup>
        <div className='row mt-3'>
          <div className='col'>
            <Label className='d-block form-check-label subtitle-container strong' for={'reporting-company'}>
              Survey Elements to Duplicate
            </Label>
            <Label className='d-block subtitle-container' for={'reporting-company'}>
              Please select which elements to copy from the current reporting survey.
            </Label>
          </div>
        </div>
        <div className='row mt-2'>
          <div className='col ml-2'>
            <CheckboxInput groupCode={checkOptions.groupCode} options={checkOptions.options} form={form} onChange={updateForm} />
          </div>
        </div>
      </FormGroup>
      <div className='text-right text-nowrap'>
        <Button color='link-secondary' onClick={toggle}>Cancel</Button>
        <SubmitButton disabled={isConfirmDisabled} className='btn-secondary' onClick={handleClick}>Create duplicate</SubmitButton>
      </div>
    </Form>
  </>
}

export default SurveyDuplicateForm;
